import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import { Logo } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { errMsg } from "@/lib/api";

const DEMO = [
  { email: "kankeualex900@gmail.com", label: "Administrateur" },
  { email: "coach.soccer@asl.test", label: "Entraîneur" },
  { email: "membre.actif@asl.test", label: "Membre actif" },
  { email: "membre.inactif@asl.test", label: "Membre inactif" },
];

export default function LoginPage() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Bienvenue, ${u.first_name} !`);
      navigate(u.role === "admin" ? "/admin" : "/tableau-de-bord");
    } catch (err) {
      toast.error(errMsg(err, "Connexion échouée"));
    } finally { setBusy(false); }
  };

  const demoLogin = async (demoEmail) => {
    setBusy(true);
    try {
      const { data } = await authApi.testLogin(demoEmail);
      localStorage.setItem("asl_token", data.token);
      setUser(data.user);
      toast.success(`Connecté en démo : ${data.user.first_name}`);
      navigate(data.user.role === "admin" ? "/admin" : "/tableau-de-bord");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden asl-hero-gradient asl-noise lg:flex lg:flex-col lg:justify-between p-10 text-white">
        <Logo size={52} light />
        <div>
          <h2 className="font-heading text-4xl font-bold">Le sport nous rassemble.</h2>
          <p className="mt-3 max-w-md text-white/70">Connectez-vous pour gérer votre adhésion, vos cotisations et suivre votre communauté sportive.</p>
        </div>
        <div className="text-sm text-white/50">Association Sports et Loisirs · Pierre-De-Saurel</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 lg:hidden"><Logo size={44} /></div>
          <h1 className="font-heading text-2xl font-bold">Connexion</h1>
          <p className="mt-1 text-sm text-muted-foreground">Accédez à votre espace membre.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Courriel</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="connexion-email-input" placeholder="vous@exemple.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="connexion-password-input" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-secondary hover:bg-emerald-600 text-white" data-testid="connexion-bouton">
              <LogIn className="mr-1.5 h-4 w-4" /> {busy ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Pas encore membre ? <Link to="/inscription" className="font-medium text-secondary hover:underline">Créer un compte</Link>
          </p>

          <div className="mt-6">
            <button type="button" onClick={() => setShowDemo((s) => !s)} className="flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="demo-toggle">
              Accès rapide (démo) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDemo ? "rotate-180" : ""}`} />
            </button>
            {showDemo && (
              <Card className="mt-2"><CardContent className="grid grid-cols-2 gap-2 p-3">
                {DEMO.map((d) => (
                  <Button key={d.email} variant="outline" size="sm" disabled={busy} onClick={() => demoLogin(d.email)} data-testid={`demo-${d.label}`}>{d.label}</Button>
                ))}
              </CardContent></Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
